import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  OnDestroy,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import * as d3 from 'd3';

import { DepartmentsService } from './departments.service';
import {
  Department,
  DepartmentTreeNode,
  DepartmentsResponse,
} from '../models/departments.model';
import { AppConfigTopNavComponent } from '../../../shared/app-config-nav/app-config-top-nav.component';
import { SearchBarComponent } from '../../../shared/search-bar/search-bar.component';

interface DepartmentFiltersVm {
  searchTerm: string;
}

@Component({
  selector: 'app-departments',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AppConfigTopNavComponent,
    RouterModule,
    SearchBarComponent,
  ],
  templateUrl: './departments.component.html',
  styleUrls: ['./departments.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DepartmentsComponent implements OnInit, OnDestroy {
  @ViewChild('treeContainer', { static: false }) treeContainer!: ElementRef;

  departments$: Observable<Department[]> = of([]);
  departmentTree: DepartmentTreeNode[] = [];
  isLoading = false;
  errorMessage: string | null = null;

  currentPage = 1;
  pageSize = 25;
  pageSizeOptions = [25, 50, 100, 500];
  totalPages = 1;

  filters: DepartmentFiltersVm = {
    searchTerm: '',
  };

  modalOpen = false;
  isEditMode = false;
  currentDepartment: Department | null = null;
  formModel: Partial<Department & { parentDepartmentId?: number | null }> =
    this.getEmptyDepartment();

  deleteConfirmOpen = false;
  departmentToDelete: Department | null = null;

  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';

  private svg!: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  private g!: d3.Selection<SVGGElement, unknown, null, undefined>;

  treeModalOpen = false;

  departmentDetailsModalOpen = false;
  departmentDetails: Department | null = null;

  constructor(
    private readonly departmentsService: DepartmentsService,
    private readonly cd: ChangeDetectorRef,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.loadDepartments();
    this.loadDepartmentTree();
  }

  ngOnDestroy(): void {
    if (this.svg) {
      this.svg.remove();
    }

    this.unlockBodyScroll();
  }

  private lockBodyScroll(): void {
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = '';
  }

  private syncBodyScrollLock(): void {
    const hasOpenModal =
      this.modalOpen ||
      this.deleteConfirmOpen ||
      this.treeModalOpen ||
      this.departmentDetailsModalOpen;

    if (hasOpenModal) {
      this.lockBodyScroll();
      return;
    }

    this.unlockBodyScroll();
  }

  loadDepartments(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.departments$ = this.departmentsService
      .getDepartments(this.currentPage, this.pageSize, 'codeAsc', {
        searchTerm: this.filters.searchTerm,
      })
      .pipe(
        map((res: DepartmentsResponse | null) => {
          if (!res) {
            this.totalPages = 1;
            return [];
          }

          this.totalPages = res.totalPages ?? 1;
          return res.data ?? [];
        }),
        catchError(() => {
          this.errorMessage = 'Failed to load departments';
          this.totalPages = 1;
          return of([]);
        }),
        finalize(() => {
          this.isLoading = false;
          this.cd.markForCheck();
        }),
      );
  }

  onSearchTermChange(value: string | null): void {
    this.filters.searchTerm = String(value ?? '').trim();
    this.currentPage = 1;
    this.loadDepartments();
  }

  loadDepartmentTree(): void {
    this.isLoading = true;

    this.departmentsService
      .getDepartmentTree()
      .pipe(
        map((tree) => (tree ?? []) as DepartmentTreeNode[]),
        catchError(() => {
          this.showToast('Failed to load department tree', 'error');
          return of([]);
        }),
        finalize(() => {
          this.isLoading = false;
          this.cd.markForCheck();
        }),
      )
      .subscribe((tree) => {
        this.departmentTree = tree;
        this.renderTree();
      });
  }

  private renderTree(): void {
    if (!this.departmentTree.length || !this.treeContainer) return;

    const containerEl = this.treeContainer.nativeElement as HTMLElement;

    const width = containerEl.offsetWidth || 1200;
    const height = containerEl.offsetHeight || 800;

    if (this.svg) {
      this.svg.remove();
    }

    this.svg = d3
      .select(containerEl)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .style('font-family', '-apple-system, BlinkMacSystemFont, sans-serif')
      .style('user-select', 'none');

    this.g = this.svg.append('g');

    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.3, 2])
      .on('zoom', (event) => {
        this.g.attr('transform', event.transform);
      });

    this.svg.call(zoom as any);

    const nodeWidth = 180;
    const nodeHeight = 60;
    const verticalSpacing = 120;
    const horizontalSpacing = 240;

    let currentXOffset = 0;

    this.departmentTree.forEach((rootNode) => {
      const root = d3.hierarchy<DepartmentTreeNode>(rootNode, (d) => d.subDepartments);

      const treeLayout = d3.tree<DepartmentTreeNode>().nodeSize([nodeWidth + 40, verticalSpacing]);

      treeLayout(root);

      let minX = Infinity;
      let maxX = -Infinity;

      root.each((d) => {
        const p = d as d3.HierarchyPointNode<DepartmentTreeNode>;
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
      });

      const treeWidth = maxX - minX;

      const treeGroup = this.g
        .append('g')
        .attr('transform', `translate(${currentXOffset - minX + 60},60)`);

      const linkPath = d3
        .linkVertical<
          d3.HierarchyPointLink<DepartmentTreeNode>,
          d3.HierarchyPointNode<DepartmentTreeNode>
        >()
        .x((d) => d.x)
        .y((d) => d.y);

      treeGroup
        .append('g')
        .selectAll<SVGPathElement, d3.HierarchyLink<DepartmentTreeNode>>('path')
        .data(root.links())
        .join('path')
        .attr('fill', 'none')
        .attr('stroke', '#d0d7e2')
        .attr('stroke-width', 2)
        .attr('d', (d) => linkPath(d as unknown as d3.HierarchyPointLink<DepartmentTreeNode>)!);

      const node = treeGroup
        .append('g')
        .selectAll<SVGGElement, d3.HierarchyNode<DepartmentTreeNode>>('g')
        .data(root.descendants())
        .join('g')
        .attr('transform', (d) => {
          const p = d as d3.HierarchyPointNode<DepartmentTreeNode>;
          return `translate(${p.x},${p.y})`;
        });

      node
        .append('rect')
        .attr('width', nodeWidth)
        .attr('height', nodeHeight)
        .attr('x', -nodeWidth / 2)
        .attr('y', -nodeHeight / 2)
        .attr('rx', 14)
        .attr('ry', 14)
        .attr('fill', '#f5f7fb')
        .attr('stroke', '#007aff')
        .attr('stroke-width', 2)
        .style('cursor', 'pointer')
        .on('click', (_, d) => this.selectDepartment(d.data));

      node
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', -4)
        .text((d) => d.data.name)
        .style('font-weight', '600')
        .style('font-size', '14px')
        .style('fill', '#1c1c1e')
        .style('pointer-events', 'none');

      node
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', 16)
        .text((d) => `${d.data.employees?.length ?? 0} Employees`)
        .style('font-size', '12px')
        .style('fill', '#636366')
        .style('pointer-events', 'none');

      currentXOffset += treeWidth + horizontalSpacing;
    });
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.currentDepartment = null;
    this.formModel = this.getEmptyDepartment();
    this.modalOpen = true;

    this.syncBodyScrollLock();
  }

  openEditModal(department: Department): void {
    this.isEditMode = true;
    this.currentDepartment = { ...department };

    this.formModel = {
      name: department.name ?? '',
      code: department.code ?? '',
      parentDepartment: department.parentDepartment ?? null,
      parentDepartmentId: department.parentDepartment?.id ?? null,
    };

    this.modalOpen = true;

    this.syncBodyScrollLock();
  }

  closeModal(): void {
    this.modalOpen = false;
    this.isEditMode = false;
    this.currentDepartment = null;
    this.formModel = this.getEmptyDepartment();

    this.syncBodyScrollLock();
  }

  submitDepartment(form?: NgForm): void {
    if (!this.formModel.name?.trim()) {
      this.showToast('Name is required', 'error');
      return;
    }

    if (!String(this.formModel.code ?? '').trim()) {
      this.showToast('Code is required', 'error');
      return;
    }

    if (this.isEditMode) {
      if (!this.currentDepartment?.id) {
        this.showToast('Unable to determine which department to update', 'error');
        return;
      }

      const payload: { name?: string; code?: string; parentDepartmentId?: number | null } = {};

      const newName = this.formModel.name.trim();
      const oldName = this.currentDepartment.name ?? '';

      const newCode = String(this.formModel.code).trim();
      const oldCode = String(this.currentDepartment.code ?? '').trim();

      const newParentId = this.formModel.parentDepartmentId ?? null;
      const oldParentId = this.currentDepartment.parentDepartment?.id ?? null;

      if (newName !== oldName) {
        payload.name = newName;
      }

      if (newCode !== oldCode) {
        payload.code = newCode;
      }

      if (newParentId !== oldParentId) {
        payload.parentDepartmentId = newParentId;
      }

      if (!Object.keys(payload).length) {
        this.showToast('No changes to update', 'error');
        return;
      }

      this.departmentsService.updateDepartment(this.currentDepartment.id, payload).subscribe({
        next: (result) => {
          if (!result.success) {
            this.showToast(result.message, 'error');
            this.cd.detectChanges();
            return;
          }

          this.closeModal();
          this.showToast(result.message, 'success');
          this.loadDepartments();
          this.loadDepartmentTree();
          this.cd.detectChanges();
        },
        error: (err) => {
          this.showToast(
            err?.error?.message || err?.message || 'Failed to update department',
            'error',
          );
          this.cd.detectChanges();
        },
      });

      return;
    }

    const createPayload = {
      name: this.formModel.name.trim(),
      code: String(this.formModel.code).trim(),
      parentDepartmentId: this.formModel.parentDepartmentId ?? null,
    };

    this.departmentsService.createDepartment(createPayload).subscribe({
      next: (result) => {
        if (!result.success) {
          this.showToast(result.message, 'error');
          this.cd.detectChanges();
          return;
        }

        this.closeModal();
        this.showToast(result.message, 'success');
        this.loadDepartments();
        this.loadDepartmentTree();
        this.cd.detectChanges();
      },
      error: (err) => {
        this.showToast(
          err?.error?.message || err?.message || 'Failed to create department',
          'error',
        );
        this.cd.detectChanges();
      },
    });
  }

  confirmDelete(department: Department): void {
    this.departmentToDelete = department;
    this.deleteConfirmOpen = true;

    this.syncBodyScrollLock();
  }

  cancelDelete(): void {
    this.deleteConfirmOpen = false;
    this.departmentToDelete = null;

    this.syncBodyScrollLock();
  }

  deleteDepartment(): void {
    if (!this.departmentToDelete) return;

    this.departmentsService.deleteDepartment(this.departmentToDelete.id).subscribe({
      next: (result) => {
        if (!result.success) {
          this.showToast(result.message, 'error');
          this.cd.detectChanges();
          return;
        }

        this.cancelDelete();
        this.showToast(result.message, 'success');
        this.loadDepartments();
        this.loadDepartmentTree();
        this.cd.detectChanges();
      },
      error: (err) => {
        this.showToast(
          err?.error?.message || err?.message || 'Failed to delete department',
          'error',
        );
        this.cd.detectChanges();
      },
    });
  }

  showToast(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;

    setTimeout(() => {
      this.toastVisible = false;
      this.cd.markForCheck();
    }, 2500);
  }

  getEmptyDepartment(): Partial<Department> {
    return { name: '', code: '', parentDepartment: null };
  }

  openTreeModal(): void {
    this.treeModalOpen = true;
    this.syncBodyScrollLock();

    setTimeout(() => this.renderTree(), 0);
  }

  closeTreeModal(): void {
    this.treeModalOpen = false;
    this.syncBodyScrollLock();
  }

  selectDepartment(node: DepartmentTreeNode): void {
    if (!node?.id) return;

    this.closeTreeModal();

    this.departmentsService.getDepartmentById(node.id).subscribe((dep) => {
      if (dep) {
        this.departmentDetails = dep;
        this.departmentDetailsModalOpen = true;
        this.syncBodyScrollLock();
        this.cd.detectChanges();
      } else {
        this.showToast('Failed to load department details', 'error');
      }
    });
  }

  closeDepartmentDetailsModal(): void {
    this.departmentDetailsModalOpen = false;
    this.departmentDetails = null;

    this.openTreeModal();
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadDepartments();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadDepartments();
    }
  }

  changePageSize(): void {
    this.currentPage = 1;
    this.loadDepartments();
  }

  goToImport(): void {
    this.router.navigate(['/settings/application/departments/import']);
  }
}